import { useState } from 'react'
import { DollarSign, FileText, TrendingUp } from 'lucide-react'
import { OrderbookItem } from '@/types/otc'
import { Modal } from './Modal'
import { Button, Input } from '@/components/atoms'
import { Form } from '@/components/molecules'
import { parseNumberInput } from '@/utils/format'
import { formatCompactNumber } from '@/utils'

export interface OrderInquiryModalProps {
  isOpen: boolean
  onClose: () => void
  order: OrderbookItem | null
  onInquirySubmitted: () => void
}

export const OrderInquiryModal = ({ isOpen, onClose, order, onInquirySubmitted }: OrderInquiryModalProps) => {
  const [formData, setFormData] = useState({
    tranche_size: '',
    valuation: ''
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!order) return

    setLoading(true)
    setError('')
    setSuccess(false)

    try {
      // Parse the input values (handles formats like 100k, 1m, 1B, 100,000, etc.)
      const parsedTrancheSize = parseNumberInput(formData.tranche_size);
      const parsedValuation = formData.valuation ? parseNumberInput(formData.valuation) : undefined;

      if (isNaN(parsedTrancheSize) || parsedTrancheSize <= 0) {
        setError('Please enter a valid amount for tranche size.');
        return;
      }

      if (parsedValuation !== undefined && (isNaN(parsedValuation) || parsedValuation <= 0)) {
        setError('Please enter a valid valuation or leave it empty.');
        return;
      }

      // TODO: Implement order inquiry submission
      // For now, we'll simulate a successful submission
      console.log('Order inquiry submitted:', {
        order_id: order.order_id,
        tranche_size: parsedTrancheSize,
        valuation: parsedValuation
      })
      
      setSuccess(true)
      onInquirySubmitted()
      
      // Close modal after 2 seconds to show success message
      setTimeout(() => {
        onClose()
        setSuccess(false)
        setFormData({ tranche_size: '', valuation: '' })
      }, 2000)
    } catch (err) {
      setError('Failed to submit order inquiry. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }))
  }

  if (!order) return null

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose}
      title="Order Inquiry"
      contentClassName="max-w-lg"
      className="bg-black/60 backdrop-blur-md"
    >
      {success ? (
        <div className="text-center py-8">
          <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <FileText size={32} className="text-green-500" />
          </div>
          <h3 className="text-lg font-semibold text-foreground mb-2">Inquiry Submitted Successfully!</h3>
          <p className="text-muted-foreground">
            Your order inquiry has been submitted. Our team will review and get back to you.
          </p>
        </div>
      ) : (
        <>
          {/* Order Info */}
          <div className="mb-6 p-4 bg-muted/50 border border-border rounded-lg">
            <div className="flex items-start gap-3 mb-3">
              {order.logo ? (
                <img src={order.logo} alt={order.project_name} className="w-10 h-10 rounded-full flex-shrink-0" />
              ) : (
                <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                  <span className="text-xs font-semibold text-muted-foreground">
                    {order.project_name.charAt(0).toUpperCase()}
                  </span>
                </div>
              )}
              <div className="flex-1 min-w-0 pr-2">
                <h3 className="font-semibold text-foreground">{order.project_name}</h3>
                <div className="text-sm text-muted-foreground">
                  {order.type} • {order.deal_type}
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-muted-foreground">Offered Amount:</span>
                <div className="font-medium text-foreground">${formatCompactNumber(order.offered_amount)}</div>
              </div>
              <div>
                <span className="text-muted-foreground">FDV:</span>
                <div className="font-medium text-foreground">${formatCompactNumber(order.offered_fully_diluted_value)}</div>
              </div>
            </div>
          </div>

          <Form onSubmit={handleSubmit}>
            {error && (
              <div className="bg-red-500/10 border border-red-500/20 text-red-500 px-4 py-3 rounded-lg mb-4">
                {error}
              </div>
            )}

            <Form.Field label="Amount / Tranche Size ($)" required>
              <Input
                type="text"
                name="tranche_size"
                value={formData.tranche_size}
                onChange={handleChange}
                placeholder="e.g., 100000, 100k, 1m, 1B"
                leftElement={<DollarSign size={16} />}
                required
              />
            </Form.Field>

            <Form.Field label="Desired Valuation ($)">
              <Input
                type="text"
                name="valuation"
                value={formData.valuation}
                onChange={handleChange}
                placeholder="e.g., 100000, 100k, 1m, 1B (optional)"
                leftElement={<TrendingUp size={16} />}
              />
            </Form.Field>

            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" onClick={onClose} className="flex-1">
                Cancel
              </Button>
              <Button type="submit" variant="primary" disabled={loading} className="flex-1">
                {loading ? 'Submitting...' : 'Submit Inquiry'}
              </Button>
            </div>
          </Form>
        </>
      )}
    </Modal>
  )
}

export default OrderInquiryModal
